import _ from 'lodash'; /* eslint-disable */
import { createContext, useContext, useMemo, useEffect, useState, useCallback } from 'react';
import { UserContext } from '@/components/App';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';

import { useClientUser } from '@/hooks';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { SET_PRICING_OPTIONS, GET_ACTIVE_INTEGRATION_OVERRIDES } from '@/components/admin/AdminPricing/graphql';
import { GET_PRICING_OVERRIDES_OPTIONS } from '@/graphql/queries/integration_meta_types';
import { useMergedOptions } from '@/components/admin/AdminPricing/hooks';

import { GET_TARIFFS } from './graphql';
import { COLUMNS } from './columns';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { client, admin_clients } = useContext(UserContext);
    const { permissions: permissionOverrides } = useClientUser();

    const [shipperOverride, setShipper] = useState(null);
    const [overrides, setOptionOverrides] = useState({});
    const [selectedOverride, setSelectedOptions] = useState(null);

    const reset = () => {
        setOptionOverrides({});
        setSelectedOptions(null);
    };

    const [getActive, { data: activeResults, loading: initActiveInflight, refetch: refetchIntegrationOverrides }] =
        useLazyQuery(GET_ACTIVE_INTEGRATION_OVERRIDES, {
            onError: (err) => {
                console.error(err);
                Sentry.captureException(err);
            },
        });
    const [getTariffs, { data: tariffResults, loading: initTariffsInflight }] = useLazyQuery(GET_TARIFFS);
    const [save, { loading: savingInflight }] = useMutation(SET_PRICING_OPTIONS, {
        onCompleted: () => {
            refetchIntegrationOverrides();
        },
    });
    const { data: optionResults, loading: initOptionsInflight } = useQuery(GET_PRICING_OVERRIDES_OPTIONS);

    const shipper = useMemo(() => {
        return shipperOverride || client.client_id;
    }, [client, shipperOverride]);

    const permissions = useMemo(() => {
        if (permissionOverrides) {
            return permissionOverrides;
        }

        const shipperObj = (admin_clients || []).find((override) => override?.client?.client_id === shipper);
        return shipperObj ? shipperObj?.client?.permissions : null;
    }, [shipper, admin_clients, permissionOverrides]);

    useEffect(() => {
        if (shipper) {
            reset();

            getActive({
                variables: {
                    client_id: shipper,
                },
            });
        }
    }, [shipper]);

    useEffect(() => {
        getTariffs({
            variables: {
                client_ids: [
                    shipper,
                    ...(permissions?.TARIFF || []).filter((p) => p.entity_type === 'CLIENT').map((p) => p.entity_id),
                ],
            },
        });
    }, [shipper, permissions]);

    const shipperOptions = useMemo(() => {
        return (admin_clients || []).length > 0
            ? admin_clients.map((mapping) => ({
                  label: mapping.client.business_name,
                  value: mapping.client.client_id,
              }))
            : null;
    }, [admin_clients]);

    const canSeeOption = useCallback(
        ({ option, tariff }) => {
            const localTariff = tariff.client_id === shipper;

            return localTariff;
        },
        [permissions, shipper]
    );

    const { pricingOptions, optionsMap, marketplaceOption, merged } = useMergedOptions({
        tariffResults,
        activeResults,
        optionResults,
        overrides,
        shipper,
        canSeeOption,
    });

    const selected = useMemo(() => {
        return selectedOverride || Object.keys(merged);
    }, [merged, selectedOverride]);

    const selectedObj = useMemo(() => {
        return selected.map((id) => optionsMap[id]).filter((option) => !!option);
    }, [selected, optionsMap]);

    const rows = useMemo(() => {
        if (!shipper) {
            return [];
        }

        return [
            marketplaceOption,
            ...Object.values(
                selected.reduce((acc, name) => {
                    const combined = {
                        ...(optionsMap[name] || {}),
                        ...(merged[name] || {}),
                        ...(overrides[name] || {}),
                    };

                    return {
                        ...acc,
                        [name]: {
                            ...combined,
                            read_only: combined.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY,
                        },
                    };
                }, {})
            ),
        ];
    }, [merged, overrides, optionsMap, selected, marketplaceOption, shipper]);

    const isValid = useMemo(() => {
        return !!marketplaceOption.tariff_id;
    }, [marketplaceOption]);

    const serialized = useMemo(() => {
        const ATTRS = [
            'name',
            'tariff_id',
            'add_margin_flat',
            'add_margin_rate',
            'zip_geofence',
            'marketplace_tier',
            'carrier_tariff_id',
        ];

        return `{${rows
            .filter((row) => row.name !== 'Marketplace')
            .map((row) => {
                return `"(${ATTRS.map((attr) => (attr === 'name' ? `\\"${row[attr]}\\"` : row[attr])).join(',')})"`;
            })
            .join(', ')}}`;
    }, [rows]);

    const callbacks = {
        selectShipper: (id) => {
            setShipper(id);
        },
        selectOption: (values) => {
            setSelectedOptions(values);
        },
        setOverride: ({ name, attr, value }) => {
            setOptionOverrides((prev) => {
                return {
                    ...prev,
                    [name]: {
                        ...prev[name],
                        [attr]: value,
                    },
                };
            });
        },
        save: () => {
            save({
                variables: {
                    client_id: shipper,
                    options: serialized,
                    marketplace_tariff_id: marketplaceOption.tariff_id,
                    marketplace_margin_flat: marketplaceOption.add_margin_flat,
                    marketplace_margin_rate: marketplaceOption.add_margin_rate,
                },
            });
        },
    };

    return (
        <Context.Provider
            value={{
                state: {
                    shipperOptions,
                    shipper,
                    selected,
                    selectedObj,
                    pricingOptions,
                    overrides,
                    hasMore: false,
                    columns: COLUMNS,
                    rows,
                    isValid,
                    height: 57,
                },
                loading: {
                    init: initOptionsInflight || initActiveInflight || initTariffsInflight,
                    save: initActiveInflight || savingInflight,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
